import { hapticFeedback } from './HapticFeedback';

/**
 * Popover service provides methods to display popover notifications
 * as an alternative to toast notifications.
 */
class Popover {
  constructor() {
    this.activePopovers = [];
    this.zIndex = 1060; // Same z-index as toasts for consistency
  }

  /**
   * Show a success popover
   * @param {Object} options - Configuration options
   * @param {string} options.title - Popover title
   * @param {string} options.message - Popover message
   * @param {number} options.duration - Duration in milliseconds (0 for no auto-close)
   * @param {HTMLElement} options.target - Target element to attach the popover to
   * @returns {Object} Popover instance with close method
   */
  async success(options = {}) {
    try {
      // Trigger haptic feedback
      await hapticFeedback.success();
      
      // Show popover with success styling
      return this.show({
        ...options,
        className: 'popover-success',
        icon: '✓'
      });
    } catch (error) {
      console.warn('Popover failed:', error);
      return { close: () => {} };
    }
  }

  /**
   * Show an error popover
   * @param {Object} options - Configuration options
   * @returns {Object} Popover instance with close method
   */
  async error(options = {}) {
    try {
      // Trigger haptic feedback
      await hapticFeedback.error();
      
      // Show popover with error styling
      return this.show({
        ...options,
        className: 'popover-error',
        icon: '✕'
      });
    } catch (error) {
      console.warn('Popover failed:', error);
      return { close: () => {} };
    }
  }

  /**
   * Show a warning popover
   * @param {Object} options - Configuration options
   * @returns {Object} Popover instance with close method
   */
  async warning(options = {}) {
    try {
      // Trigger haptic feedback
      await hapticFeedback.warning();
      
      // Show popover with warning styling
      return this.show({
        ...options,
        className: 'popover-warning',
        icon: '⚠'
      });
    } catch (error) {
      console.warn('Popover failed:', error);
      return { close: () => {} };
    }
  }

  /**
   * Show an info popover
   * @param {Object} options - Configuration options
   * @returns {Object} Popover instance with close method
   */
  async info(options = {}) {
    try {
      // Trigger haptic feedback
      await hapticFeedback.medium();
      
      // Show popover with info styling
      return this.show({
        ...options,
        className: 'popover-info',
        icon: 'ℹ'
      });
    } catch (error) {
      console.warn('Popover failed:', error);
      return { close: () => {} };
    }
  }

  /**
   * Show a popover
   * @param {Object} options - Configuration options
   * @returns {Object} Popover instance with close method
   */
  show(options = {}) {
    // Set default options
    const config = {
      title: '',
      message: '',
      duration: 3000,
      target: document.body,
      position: 'top-end',
      className: '',
      icon: '',
      ...options
    };

    // Create popover element
    const popover = document.createElement('div');
    popover.className = `popover-notification ${config.className}`;
    popover.style.position = 'fixed';
    popover.style.zIndex = (this.zIndex++).toString();
    popover.style.padding = '10px 15px';
    popover.style.borderRadius = '4px';
    popover.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
    popover.style.backgroundColor = '#fff';
    popover.style.maxWidth = '300px';
    popover.style.overflow = 'hidden';
    popover.style.transition = 'all 0.3s ease';
    popover.style.opacity = '0';
    popover.style.transform = 'translateY(-20px)';

    // Set position based on config
    this.setPosition(popover, config.position, config.target);

    // Create content
    let content = '';
    if (config.icon) {
      content += `<div class="popover-icon" style="display: inline-block; margin-right: 10px; font-size: 18px;">${config.icon}</div>`;
    }
    content += '<div style="display: inline-block; vertical-align: top;">';
    if (config.title) {
      content += `<div class="popover-title" style="font-weight: bold; margin-bottom: 5px;">${config.title}</div>`;
    }
    if (config.message) {
      content += `<div class="popover-message">${config.message}</div>`;
    }
    content += '</div>';
    content += '<button class="popover-close" style="position: absolute; top: 5px; right: 5px; background: none; border: none; cursor: pointer; font-size: 16px;">×</button>';
    
    popover.innerHTML = content;

    // Add to DOM
    document.body.appendChild(popover);
    this.activePopovers.push(popover);

    // Show with animation
    setTimeout(() => {
      popover.style.opacity = '1';
      popover.style.transform = 'translateY(0)';
    }, 10);

    // Set up close button
    const closeButton = popover.querySelector('.popover-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.close(popover));
    }

    // Auto-close if duration is set
    let timeoutId = null;
    if (config.duration > 0) {
      timeoutId = setTimeout(() => this.close(popover), config.duration);
    }

    // Return object with close method
    return {
      close: () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        this.close(popover);
      }
    };
  }

  /**
   * Close a popover
   * @param {HTMLElement} popover - The popover element to close
   */
  close(popover) {
    if (!popover) return;

    // Hide with animation
    popover.style.opacity = '0';
    popover.style.transform = 'translateY(-20px)';

    // Remove after animation
    setTimeout(() => {
      if (popover.parentNode) {
        popover.parentNode.removeChild(popover);
      }
      this.activePopovers = this.activePopovers.filter(p => p !== popover);
    }, 300);
  }

  /**
   * Close all active popovers
   */
  closeAll() {
    [...this.activePopovers].forEach(popover => this.close(popover));
  }

  /**
   * Set the position of the popover
   * @param {HTMLElement} popover - The popover element
   * @param {string} position - Position ('top', 'top-start', 'top-end', 'bottom', etc.)
   * @param {HTMLElement} target - Target element to position relative to
   */
  setPosition(popover, position, target) {
    // Default to fixed position in top-right corner
    popover.style.top = '20px';
    popover.style.right = '20px';

    // If target is document.body, use fixed positioning
    if (target === document.body) {
      popover.style.position = 'fixed';
      
      // Position based on position string
      switch (position) {
        case 'top-start':
          popover.style.top = '20px';
          popover.style.left = '20px';
          popover.style.right = 'auto';
          break;
        case 'top':
          popover.style.top = '20px';
          popover.style.left = '50%';
          popover.style.right = 'auto';
          popover.style.transform = 'translateX(-50%)';
          break;
        case 'top-end':
          popover.style.top = '20px';
          popover.style.right = '20px';
          break;
        case 'bottom-start':
          popover.style.top = 'auto';
          popover.style.bottom = '20px';
          popover.style.left = '20px';
          popover.style.right = 'auto';
          break;
        case 'bottom':
          popover.style.top = 'auto';
          popover.style.bottom = '20px';
          popover.style.left = '50%';
          popover.style.right = 'auto';
          popover.style.transform = 'translateX(-50%)';
          break;
        case 'bottom-end':
          popover.style.top = 'auto';
          popover.style.bottom = '20px';
          popover.style.right = '20px';
          break;
      }
    } else {
      // If target is a specific element, position relative to it
      popover.style.position = 'absolute';
      
      const targetRect = target.getBoundingClientRect();
      const bodyRect = document.body.getBoundingClientRect();
      
      switch (position) {
        case 'top':
          popover.style.bottom = (bodyRect.height - targetRect.top + 10) + 'px';
          popover.style.left = (targetRect.left + targetRect.width / 2) + 'px';
          popover.style.transform = 'translateX(-50%)';
          break;
        case 'bottom':
          popover.style.top = (targetRect.bottom + 10) + 'px';
          popover.style.left = (targetRect.left + targetRect.width / 2) + 'px';
          popover.style.transform = 'translateX(-50%)';
          break;
        case 'left':
          popover.style.right = (bodyRect.width - targetRect.left + 10) + 'px';
          popover.style.top = (targetRect.top + targetRect.height / 2) + 'px';
          popover.style.transform = 'translateY(-50%)';
          break;
        case 'right':
          popover.style.left = (targetRect.right + 10) + 'px';
          popover.style.top = (targetRect.top + targetRect.height / 2) + 'px';
          popover.style.transform = 'translateY(-50%)';
          break;
      }
    }
  }
}

export const popover = new Popover();